import { Location } from '@angular/common'
import { Injectable, inject } from '@angular/core'
import { Params, Router } from '@angular/router'
import { ElderToastService, IframeService, Interval } from '@elderbyte/ngx-starter'
import { Instant } from '@js-joda/core'
import { Observable } from 'rxjs'
import { MexusViewMode } from '../../context/view-mode-context/mexus-view-mode'
import { ViewModeContextService } from '../../context/view-mode-context/view-mode-context.service'
import { WorkOrder } from '../../orders/work-orders/work-order'
import { ControlCenter } from '../../shopfloor/ccs/control-center'
import { ControlCenterService } from '../../shopfloor/ccs/control-center.service'
import { WorkCenter } from '../../shopfloor/workcenters/work-center'
import { ControlTerminalMode } from '../control-terminal-mode'

export class StorageTerminalCtx {}

export class WorkStationTerminalCtx {
  public static live(
    currentTime?: Instant,
    workCenterId?: string,
    workOrderId?: string,
    privileged?: boolean
  ): WorkStationTerminalCtx {
    return new WorkStationTerminalCtx(
      ControlTerminalMode.LIVE,
      currentTime,
      undefined,
      workCenterId,
      workOrderId,
      privileged
    )
  }

  public static preview(
    interval?: Interval,
    workCenterId?: string,
    workOrderId?: string,
    privileged?: boolean
  ): WorkStationTerminalCtx {
    return new WorkStationTerminalCtx(
      ControlTerminalMode.PREVIEW,
      undefined,
      interval,
      workCenterId,
      workOrderId,
      privileged
    )
  }

  constructor(
    public mode: ControlTerminalMode,
    public currentTime?: Instant,
    public interval?: Interval,
    public workCenterId?: string,
    public workOrderId?: string,
    public privileged?: boolean
  ) {}
}

export class TerminalCtx {
  public workStationCtx: WorkStationTerminalCtx
  public storageCtx: StorageTerminalCtx

  public static workStation(
    controlCenterId: string,
    workStationTerminalCtx: WorkStationTerminalCtx
  ): TerminalCtx {
    const ctx = new TerminalCtx(controlCenterId)
    ctx.workStationCtx = workStationTerminalCtx
    return ctx
  }

  public static storage(controlCenterId: string, storageTerminalCtx: StorageTerminalCtx) {
    const ctx = new TerminalCtx(controlCenterId)
    ctx.storageCtx = storageTerminalCtx
    return ctx
  }

  constructor(public controlCenterId: string) {}
}

@Injectable({
  providedIn: 'root',
})
export class ControlTerminalDialogService {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly iframeService = inject(IframeService)
  private readonly controlCenterService = inject(ControlCenterService)
  private readonly toastService = inject(ElderToastService)
  private router = inject(Router)
  private location = inject(Location)
  private viewModeContextService = inject(ViewModeContextService)

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public openTerminalForWorkCenter(
    workCenter: WorkCenter,
    interval: Interval,
    privileged?: boolean
  ): void {
    this.openWorkStationTerminal(workCenter, interval, undefined, privileged)
  }

  public openTerminalForWorkCenterWithOrder(
    workCenter: WorkCenter,
    workOrder: WorkOrder,
    privileged?: boolean
  ): void {
    this.openWorkStationTerminal(
      workCenter,
      Interval.parse(workOrder.scheduleInterval),
      workOrder.id,
      privileged
    )
  }

  public openWorkStationTerminal(
    workCenter: WorkCenter,
    interval: Interval,
    workOrderId?: string,
    privileged?: boolean
  ): void {
    this.controlCenterService.findControlCentersFor(workCenter).subscribe((ccs) => {
      if (ccs && ccs.length > 0) {
        const cc = this.selectControlCenter(ccs)
        const ctx =
          this.viewModeContextService.viewMode === MexusViewMode.LIVE
            ? WorkStationTerminalCtx.live(Instant.now(), workCenter.id, workOrderId, privileged)
            : WorkStationTerminalCtx.preview(interval, workCenter.id, workOrderId, privileged)
        this.openTerminalDialog(TerminalCtx.workStation(cc.id, ctx))
      } else {
        this.toastService.info(
          'Could not find any Control Center which controls WorkCenter ' + workCenter.name
        )
      }
    })
  }

  public openTerminalDialog(ctx: TerminalCtx): Observable<unknown> {
    return this.iframeService.openIframeDialog<void>({
      srcUrl: this.buildTerminalUrl(ctx),
    })
  }

  /***************************************************************************
   *                                                                         *
   * Private methods                                                         *
   *                                                                         *
   **************************************************************************/

  private selectControlCenter(ccs: ControlCenter[]): ControlCenter {
    return ccs[0] // TODO dialog if more than one
  }

  private buildTerminalUrl(ctx: TerminalCtx): string {
    let url

    if (ctx.workStationCtx) {
      url = this.buildWorkStationTerminalUrl(ctx.controlCenterId, ctx.workStationCtx)
    } else {
      url = this.buildStorageTerminalUrl(ctx.controlCenterId, ctx.storageCtx)
    }

    return url
  }

  private buildWorkStationTerminalUrl(
    controlCenterId: string,
    ctx: WorkStationTerminalCtx
  ): string {
    const queryParams = {
      mode: ctx.mode.toString(),
      privileged: ctx.privileged,
    } as Params

    switch (ctx.mode) {
      case ControlTerminalMode.LIVE:
        queryParams['currentTime'] = ctx.currentTime ? ctx.currentTime.toString() : null
        break
      case ControlTerminalMode.PREVIEW:
        queryParams['terminalInterval'] = ctx.interval ? ctx.interval.toInstantString() : null
    }

    return this.buildUrl(controlCenterId, ctx.workCenterId, ctx.workOrderId, queryParams)
  }

  private buildStorageTerminalUrl(controlCenterId: string, ctx: StorageTerminalCtx) {
    return this.buildUrl(controlCenterId)
  }

  private buildUrl(
    controlCenterId: string,
    workCenterId?: string,
    workOrderId?: string,
    queryParams?: Params
  ) {
    const segments = ['app', 'terminals', controlCenterId]

    if (workCenterId) {
      segments.push('work-centers', workCenterId, 'orders')

      if (workOrderId) {
        segments.push(workOrderId)
      }
    }

    const tree = this.router.createUrlTree(
      [
        {
          outlets: { primary: segments, side: null },
        },
      ],
      { queryParams: queryParams }
    )

    const treeStr = tree.toString()

    const path = this.location.prepareExternalUrl(treeStr)

    return window.location.origin + path
  }
}
